import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  loading: boolean;
  error: Error | null;
  data: T | null;
}

/**
 * Run an async loader on mount and whenever `key` changes (or `reload()` is called), tracking
 * loading/error/data. `key` is a primitive that captures the loader's inputs (e.g. a siteId), so
 * a changing closure identity doesn't re-fire the effect. `reload` re-runs the loader — call it
 * after a mutation to refresh the view.
 */
export function useFetch<T>(
  load: () => Promise<T>,
  key = '',
): AsyncState<T> & { reload: () => void } {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<AsyncState<T>>({ loading: true, error: null, data: null });
  const previousKey = useRef(key);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `load` is re-created each render; `key` + `tick` drive refetch.
  useEffect(() => {
    let alive = true;
    const keyChanged = previousKey.current !== key;
    previousKey.current = key;
    // Keep already-rendered data in place during an explicit poll/reload. This avoids flashing the
    // whole view back to "Loading…" while first-run watches for enrollment and sync signals. A
    // changed input key still clears stale data from the previous resource.
    setState((current) => ({
      loading: keyChanged || current.data === null,
      error: null,
      data: keyChanged ? null : current.data,
    }));
    load().then(
      (data) => {
        if (alive) setState({ loading: false, error: null, data });
      },
      (error: Error) => {
        if (alive) setState({ loading: false, error, data: null });
      },
    );
    return () => {
      alive = false;
    };
  }, [key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}
